import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, extractData } from '@/lib/treaty';

type UserEntityPermissionsParams = {
  tenantId: string;
  userId: string;
};

const getUserEntityPermissions = ({ tenantId, userId }: UserEntityPermissionsParams) => {
  return extractData(api.tenants({ id: tenantId }).permissions({ userId }).get());
};

export const useUserEntityPermissions = ({ tenantId, userId }: UserEntityPermissionsParams) => {
  return useQuery({
    queryKey: ['tenants', tenantId, 'permissions', userId],
    queryFn: () => getUserEntityPermissions({ tenantId, userId }),
    enabled: Boolean(tenantId && userId),
  });
};

type UpdateUserPermissionsPayload = Parameters<
  NonNullable<ReturnType<NonNullable<ReturnType<typeof api.tenants>>['permissions']>>['put']
>[0];

export type ApiPermission =
  UpdateUserPermissionsPayload['permissions'][keyof UpdateUserPermissionsPayload['permissions']];

type UpdateUserEntityPermissionsParams = {
  tenantId: string;
  userId: string;
  payload: UpdateUserPermissionsPayload;
};

const updateUserEntityPermissions = ({
  tenantId,
  userId,
  payload,
}: UpdateUserEntityPermissionsParams) => {
  return extractData(api.tenants({ id: tenantId }).permissions({ userId }).put(payload));
};

export const useUpdateUserEntityPermissions = ({
  tenantId,
  userId,
}: {
  tenantId: string;
  userId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateUserPermissionsPayload) =>
      updateUserEntityPermissions({ tenantId, userId, payload }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tenants', tenantId, 'permissions', userId],
      });
    },
  });
};
